import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { message, Table, Form, Popconfirm, Button, Tooltip, Card } from "antd";
import styles from "./maintenance.module.scss";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  EditOutlined,
  LinkOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { BsCheckLg } from "react-icons/bs";

import { useAppDispatch, useAppSelector } from "store/hook";
import { setDeviceId, setRecord, setTicketVisible, setRemoveDeviceModalVisible } from "Components/Go/goSlice";
import { setLogVisible } from "Components/Log/logSlice";
import { useGetList, useDeleteTicket, useUpdateTicket } from "./queries";
import { useSetting } from "Components/Header/queries";

import CreateTicketModal from "./Components/CreateTicket_Modal";
import EditTicketModal from "./Components/EditTicket_Modal";
import LogModal from "../Log";
import { TimestampToDateOnly } from "Utility/Fetch";
import { config } from "cypress/types/bluebird";

export interface ticketRecordProps {
  cid: string | undefined;
  list?: {
    _id?: string;
    ticket?: string;
    desc?: string;
    id?: any;
    expire_utc: number;
    active?: boolean | undefined;
    maintainer?: string;
    osmart_url?: string;
    progress?: any;
  };
}

const Maintenance = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [CreateVisible, setCreateVisible] = useState(false);
  const [editTicketVisible, setEditTicketVisible] = useState(false);
  const [clipboard, setClipboard] = useState(false);
  const [NotifiModalvisible, setNotifiModalvisible] = useState(false);
  const { stateCid, level, loginCid, time_zone, user, isLogin } = useAppSelector((state) => state.global);
  const { record, ticketData, ticketVisible } = useAppSelector((state) => state.goPage);
  const { t, i18n } = useTranslation();

  const { data: getListData, isLoading: listLoading } = useGetList(level, stateCid);
  const { mutateAsync: deleteTicketMutation, isLoading: deleteTicketLoading } = useDeleteTicket();
  const { mutateAsync: updateTicketMutation, isLoading: updateTicketLoading } = useUpdateTicket();

  const forbiddenLevel = level === "get" || (level === "super_super" && stateCid === "");
  const loading = listLoading || deleteTicketLoading || updateTicketLoading;

  const { data: userSettingData } = useSetting(user, level, isLogin);

  // dataSource
  const deepCopyGetListData = getListData && JSON.parse(JSON.stringify(getListData));
  let tableData: any = [];
  deepCopyGetListData?.map((item: any) => {
    item?.list?.map((element: any) => {
      let data: any = {};
      // let logArray = Object.entries(element.log);
      // let checkNum = 0;
      // logArray.map((value: any) => {
      //   if (value[1].issue_check === true) checkNum++;
      // });

      data.cid = item.cid;
      data.list = element;
      data.list.ticket = element.ticket;
      data.list.osmart_url = element.osmart_url;
      data.list.device_url = element.device_url;
      data.list.progress_string = `${element.progress.new.done + element.progress.fix.done}/${
        element.progress.new.total + element.progress.fix.total
      }`;
      data.list.progress_rate =
        (element.progress.new.done + element.progress.fix.done) /
        (element.progress.new.total + element.progress.fix.total);
      // data.list.expiration = moment(element?.expire_utc * 1000).format("YYYY-MM-DD");
      data.list.expiration = element?.expire_utc;
      tableData.push(data);
    });
  });

  // const deleteTicket = (record: ticketRecordProps) => {
  const deleteTicket = (record: any) => {
    deleteTicketMutation({ _id: record.list?._id })
      .then(() => {
        message.success(t("ISMS.Success"));
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  // const updateActive = (record: ticketRecordProps) => {
  const updateActive = (record: any) => {
    updateTicketMutation(record)
      .then(() => {
        message.success(t("ISMS.Success"));
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  const onClipboard = (record: ticketRecordProps) => {
    navigator.clipboard.writeText(`${record?.list?.osmart_url}`);
  };

  const columns = [
    {
      title: t("ISMS.Ticket"),
      dataIndex: ["list", "ticket"],
      key: "ticket",
      width: 180,
      sorter: (a: any, b: any) =>
        b.list.ticket.slice(0, 14).replaceAll("-", "") - a.list.ticket.slice(0, 14).replaceAll("-", ""),
      render: (_: string, record: ticketRecordProps) => {
        return (
          <Link
            to={`/osmartGO/${record.list?.ticket}`}
            onClick={() => {
              dispatch(setRecord(record));
            }}
          >
            {record.list?.ticket?.slice(0, 14)}
          </Link>
        );
      },
    },
    {
      title: t("ISMS.Description"),
      dataIndex: ["list", "desc"],
      key: "description",
    },
    {
      title: t("ISMS.URL"),
      dataIndex: ["list", "osmart_url"],
      key: "osmart_url",
    },
    {
      title: t("ISMS.Progress"),
      dataIndex: ["list", "progress_string"],
      key: "progress",
      sorter: (a: any, b: any) => parseInt(a.list.progress_string) - parseInt(b.list.progress_string),
      render: (progress_string: any) => {
        return progress_string;
      },
    },
    {
      title: t("ISMS.Expiration"),
      dataIndex: ["list", "expiration"],
      key: "expiration",
      sorter: (a: any, b: any) => a.list.expiration - b.list.expiration,
      render: (timestamp: any) => {
        return TimestampToDateOnly(timestamp * 1000, time_zone);
      },
    },
    {
      title: t("ISMS.Status"),
      dataIndex: "status",
      key: "status",
      filters: [
        { text: `${t("ISMS.Inactive")}`, value: `${t("ISMS.Inactive")}` },
        { text: `${t("ISMS.Completed")}`, value: `${t("ISMS.Completed")}` },
        { text: `${t("ISMS.Expired")}`, value: `${t("ISMS.Expired")}` },
        { text: `${t("ISMS.In Progress")}`, value: `${t("ISMS.In Progress")}` },
      ],
      onFilter: (value: string, record: any) => {
        if (value === `${t("ISMS.Inactive")}`) {
          return !record?.list?.active && record.list?.expire_utc > moment().unix();
        } else if (value === `${t("ISMS.Expired")}`) {
          return record?.list?.expire_utc < moment().unix();
        } else if (value === `${t("ISMS.Completed")}`) {
          return record?.list?.progress_rate === 1;
        } else if (value === `${t("ISMS.In Progress")}`) {
          return record?.list?.progress_rate < 1 && record?.list?.active && record.list?.expire_utc > moment().unix();
        }
      },
      render: (_: string, record: ticketRecordProps) => {
        // 進行中, 逾期, 已完成, active
        let status = "";
        // @ts-ignore
        if (!record?.list?.active && record.list?.expire_utc > moment().unix()) {
          status = t("ISMS.Inactive");
        } else if (
          record?.list?.progress.new.done + record?.list?.progress.fix.done ===
          record?.list?.progress.new.total + record?.list?.progress.fix.total
        ) {
          status = t("ISMS.Completed");
          // @ts-ignore
        } else if (record?.list?.expire_utc < moment().unix()) {
          status = t("ISMS.Expired");
        } else {
          status = t("ISMS.In Progress");
        }
        return status;
      },
    },
    {
      title: t("ISMS.Operation"),
      dataIndex: "operation",
      key: "operation",
      render: (_: string, record: ticketRecordProps) => {
        const expire_utc = record.list?.expire_utc || 0;
        const date = Math.floor(Date.now()) / 1000;

        return (
          <div className={styles.InformationBtnWrapper}>
            <Tooltip title={t("ISMS.EditTicket")}>
              <Button
                type="text"
                onClick={() => {
                  dispatch(setRecord(record));
                  setEditTicketVisible(true);
                }}
                icon={<EditOutlined />}
                disabled={forbiddenLevel}
              ></Button>
            </Tooltip>

            <Tooltip title={clipboard ? t("ISMS.URLCopied") : t("ISMS.CopyURL")}>
              <Button
                type="text"
                onClick={() => {
                  onClipboard(record);
                  setClipboard(true);
                }}
                onMouseEnter={() => {
                  setClipboard(false);
                }}
                icon={<LinkOutlined />}
              ></Button>
            </Tooltip>

            {/* <Tooltip title={t("ISMS.Log")}>
              <Button
                type="text"
                onClick={() => {
                  dispatch(setRecord({}));
                  dispatch(setRecord(record));
                  dispatch(setLogVisible(true));
                }}
                icon={<ProfileOutlined />}
              ></Button>
            </Tooltip> */}

            {record?.list?.active ? (
              <Tooltip title={t("ISMS.Disable")}>
                <Button
                  type="text"
                  onClick={() => {
                    updateActive(record);
                  }}
                  icon={<PauseCircleOutlined />}
                  disabled={expire_utc <= date}
                ></Button>
              </Tooltip>
            ) : (
              <Tooltip title={t("ISMS.Enable")}>
                <Button
                  type="text"
                  onClick={() => {
                    updateActive(record);
                  }}
                  icon={<PlayCircleOutlined />}
                  disabled={expire_utc <= date}
                ></Button>
              </Tooltip>
            )}

            {level === "super_super" || level === "super" || level === "admin" ? (
              <Tooltip title={t("ISMS.DeleteTicket")}>
                <Popconfirm
                  title={t("ISMS.Sure to delete?")}
                  okText={t("ISMS.OK")}
                  cancelText={t("ISMS.Cancel")}
                  onConfirm={() => {
                    deleteTicket(record);
                  }}
                >
                  <Button icon={<DeleteOutlined />} type="text"></Button>
                </Popconfirm>
              </Tooltip>
            ) : (
              ""
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(setDeviceId(""));
    dispatch(setTicketVisible(false));
    i18n.changeLanguage(userSettingData?.language);
    dispatch(setRemoveDeviceModalVisible(false));
  }, []);

  return (
    <>
      <div>
        {level === "super_super" || level === "super" || level === "admin" ? (
          <CreateTicketModal CreateVisible={CreateVisible} setCreateVisible={setCreateVisible} />
        ) : (
          ""
        )}

        {level === "super_super" || level === "super" || level === "admin" ? (
          <EditTicketModal
            editTicketVisible={editTicketVisible}
            setEditTicketVisible={setEditTicketVisible}
            // record={record}
            // setRecord={setRecord}
          />
        ) : (
          ""
        )}

        {/* <LogModal /> */}

        <Card>
          {(level === "super_super" || level === "super" || level === "admin") && (
            <Button
              type="primary"
              onClick={() => setCreateVisible(true)}
              className={styles.NewUserInfoBtn}
              loading={loading}
              disabled={forbiddenLevel}
            >
              {t("ISMS.CreateTicket")}
            </Button>
          )}

          {deleteTicketLoading && (
            <span style={{ fontWeight: 600, color: "blue" }}>deleting customer, please wait.</span>
          )}

          <Form form={form} component={false} layout="vertical">
            <Table
              className={styles.UserInfoTable}
              loading={loading}
              // @ts-ignore
              columns={columns}
              dataSource={tableData.reverse() || []}
              // @ts-ignore
              rowKey={(record) => record.list?._id}
            />
          </Form>
        </Card>
      </div>
    </>
  );
};

export default Maintenance;
